import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Layers, CheckCircle2, Cog, ChevronRight, RefreshCw, Plus, Factory } from 'lucide-react';

import { Screen, ScreenSplit, Pane, CommandBar, StatStrip, DataGrid } from '@/components/data';

const STAGE_COLORS = {
  green: '#16a34a',
  blue: '#2563eb',
  gray: '#6b7280',
  orange: '#ea580c',
};

export default function SupervisorDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [jobCards, setJobCards] = useState([]);
  const [stats, setStats] = useState({});
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, jcRes] = await Promise.all([
        api.get('/dashboard/supervisor'),
        api.get('/supervisor/job-cards?tab=all&limit=20'),
      ]);
      setDashboard(dashRes.data);
      setJobCards(jcRes.data.job_cards || []);
      setStats(jcRes.data.stats || {});
      setStageCounts(jcRes.data.stage_counts || {});
    } catch (e) {
      toast.error('Failed to load dashboard data');
    }
    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  // Group job cards by order for dashboard display
  const groupedOrders = React.useMemo(() => {
    const groups = {};
    for (const card of jobCards) {
      const key = card.order_uuid || card.order_id;
      if (!groups[key]) {
        groups[key] = {
          order_uuid: card.order_uuid,
          order_id: card.order_id,
          customer_name: card.customer_name,
          firm_name: card.firm_name || '',
          priority: card.priority,
          delivery_date: card.delivery_date || '',
          cards: [],
        };
      }
      groups[key].cards.push(card);
    }
    return Object.values(groups).slice(0, 10);
  }, [jobCards]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const kpis = dashboard?.kpis || {};
  const progress = dashboard?.today_progress || {};

  // Job cards with their order as a group row above them — a supervisor works a
  // card, but reads the order it belongs to, and both fit in one grid.
  const rows = [];
  for (const group of groupedOrders) {
    rows.push({
      __rowId: `o:${group.order_uuid || group.order_id}`,
      __full: true,
      __render: () => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-[12px] font-semibold text-slate-800">{group.customer_name}</span>
          {group.firm_name && <span className="text-[11px] text-slate-500">{group.firm_name}</span>}
          <span className="font-mono text-[10px] text-slate-400">{group.order_id}</span>
          <span className="text-[10px] text-slate-400">{group.cards.length} card{group.cards.length === 1 ? '' : 's'}</span>
          {group.priority && (
            <span className={`text-[10px] font-medium ${
              group.priority === 'high' ? 'text-red-600'
                : group.priority === 'low' ? 'text-emerald-600' : 'text-amber-600'
            }`}>{group.priority}</span>
          )}
          {group.delivery_date && (
            <span className="text-[10px] text-slate-400">
              due {formatIST(group.delivery_date, { dateOnly: true })}
            </span>
          )}
        </span>
      ),
    });
    for (const card of group.cards) {
      rows.push({ ...card, __depth: 1, __rowId: card.row_key });
    }
  }

  const columns = [
    {
      key: 'item',
      header: 'Item',
      width: 'minmax(12rem,1.5fr)',
      render: (card) => (
        <span className="block truncate text-slate-700">
          {card.item_name || card.product_subtype || 'Item'}
        </span>
      ),
    },
    {
      key: 'current_stage_name',
      header: 'Stage',
      width: '10rem',
      render: (card) => (
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: STAGE_COLORS[card.stage_color] || STAGE_COLORS.blue }} />
          <span className="truncate text-[12px] font-medium text-slate-700">{card.current_stage_name || '—'}</span>
        </span>
      ),
    },
    {
      key: 'machine_name',
      header: 'Machine',
      width: '9rem',
      mobile: false,
      render: (card) => card.machine_name ? (
        <span className="flex min-w-0 items-center gap-1 text-[12px] text-slate-600">
          <Cog className="h-3 w-3 shrink-0 text-slate-400" />
          <span className="truncate">{card.machine_name}</span>
        </span>
      ) : (
        <span className="text-[11px] font-medium text-amber-600">Unassigned</span>
      ),
    },
    {
      key: 'quantity',
      header: 'Qty',
      align: 'right',
      width: '5.5rem',
      render: (card) => <span className="font-medium">{card.quantity?.toLocaleString() || '—'}</span>,
    },
    {
      key: 'progress',
      header: 'Progress',
      width: '8rem',
      mobile: false,
      render: (card) => {
        const done = card.completed_stages || 0;
        const totalStages = card.total_stages || 0;
        const pct = totalStages ? Math.round((done / totalStages) * 100) : 0;
        return (
          <span className="flex items-center gap-1.5">
            <Progress value={pct} className="h-1.5 w-12" />
            <span className="text-[10px] text-slate-500">{done}/{totalStages}</span>
          </span>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      width: '3rem',
      align: 'right',
      render: () => <ChevronRight className="ml-auto h-3.5 w-3.5 text-slate-300" />,
    },
  ];

  return (
    <Screen data-testid="supervisor-dashboard-page">
      <CommandBar
        title={`${getGreeting()}, ${user?.full_name || ''}`}
        subtitle={formatIST(new Date().toISOString(), { dateOnly: true })}
        actions={[
          { key: 'refresh', label: 'Refresh', icon: RefreshCw, onClick: handleRefresh, disabled: refreshing },
          { key: 'queue', label: 'Production Queue', icon: Factory, onClick: () => navigate('/supervisor/production-queue') },
          { key: 'cards', label: 'All Job Cards', icon: Layers, onClick: () => navigate('/supervisor/job-cards') },
          { key: 'new', label: 'New Order', icon: Plus, tone: 'accent', onClick: () => navigate('/supervisor/orders/new') },
        ]}
      />

      <StatStrip
        stats={[
          { key: null, label: 'active cards', value: kpis.active_job_cards ?? stats.active ?? 0, tone: 'blue' },
          { key: null, label: 'awaiting machine', value: kpis.pending_assignment ?? stats.pending_assignment ?? 0,
            tone: (kpis.pending_assignment ?? stats.pending_assignment) ? 'warn' : 'good' },
          { key: null, label: 'done today', value: progress.completed_today ?? 0, tone: 'good' },
          { key: null, label: 'overdue', value: kpis.overdue ?? 0, tone: kpis.overdue ? 'danger' : 'good' },
          { key: null, label: 'quality flags', value: kpis.quality_flags ?? 0, tone: kpis.quality_flags ? 'danger' : 'default' },
        ]}
      />

      <ScreenSplit
        railWidth="17rem"
        rail={
          <Pane title="Stage distribution" aside="cards standing at each" className="max-h-full">
            {Object.keys(stageCounts).length === 0 ? (
              <p className="p-4 text-center text-xs text-slate-400">Nothing on the floor</p>
            ) : Object.entries(stageCounts)
              .sort((a, b) => b[1] - a[1])
              .map(([stage, count]) => (
                <button
                  key={stage}
                  type="button"
                  onClick={() => navigate(`/supervisor/job-cards?stage=${encodeURIComponent(stage)}`)}
                  className="flex w-full items-center justify-between gap-2 border-b border-slate-100 px-2.5 py-1.5 text-left transition-colors last:border-b-0 hover:bg-slate-50"
                >
                  <span className="truncate text-[13px] text-slate-700">{stage}</span>
                  <span className="shrink-0 text-[12px] font-semibold tabular-nums text-brand-blue">{count}</span>
                </button>
              ))}
          </Pane>
        }
      >
        <Pane
          title="Active job cards"
          aside={`${jobCards.length} across ${groupedOrders.length} order${groupedOrders.length === 1 ? '' : 's'}`}
          scroll={false}
          bodyClassName="flex flex-col"
        >
          <DataGrid
            data-testid="supervisor-dashboard-cards"
            columns={columns}
            rows={rows}
            getRowId={(r) => r.__rowId}
            onRowClick={(r) => !r.__full && navigate(`/supervisor/job-card/${r.line_item_id}`)}
            empty={
              <span className="flex flex-col items-center gap-2 text-slate-400">
                <CheckCircle2 className="h-8 w-8 text-emerald-300" />
                Nothing active — the floor is clear.
              </span>
            }
          />
        </Pane>
      </ScreenSplit>
    </Screen>
  );
}
